import { ContactDetails } from '@/components/Atoms/ContactDetails/ContactDetails';
import { Notification } from '@/components/Atoms/Notification/Notification';
import { Prose } from '@/components/Atoms/Prose/Prose';
import { H5 } from '@/components/Core/Heading';
import { CenteredPageContent } from '@/components/Layouts/CenteredPageContent';
import { breakpoints } from '@/styles/media';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { DebugInfo } from './DebugInfo';

interface IProps {
    title?: string;
    subtitle?: string;
    message?: string | ReactNode;
    showDebugInfo?: boolean;
}

const defaultTitle = 'Error';
const defaultMessage = `We are terribly sorry, an unrecoverable error has occurred. Please refresh the page.
 If the problem persists, contact client inquiries for assistance.`;

const StyledErrorPage = styled.div`
    padding: var(--size-grid-double) var(--size-grid-unit);
    max-width: var(--size-maxProseColumnWidth);
    margin: 0 auto;
    text-align: center;
`;

const StyledContactDetails = styled(ContactDetails)`
    margin-top: var(--size-grid-quarter);
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    > li {
        margin: 0 var(--size-grid-half);
    }
    > li:last-child {
        flex-basis: 100%;
        margin: var(--size-grid-quarter) 0 var(--size-grid-unit);
        ${breakpoints.M`
            margin-bottom: var(--size-grid-half);
        `}
    }
`;

const Intro = styled.div`
    margin-bottom: var(--size-grid-double);
`;

const Title = styled.h1`
    color: var(--color-body-accent);
    margin-bottom: var(--size-grid-unit);
`;

const Message = styled.p`
    white-space: pre-line;
`;

export const ErrorPage = ({ title = defaultTitle, subtitle, message = defaultMessage, showDebugInfo }: IProps) => (
    <CenteredPageContent>
        <StyledErrorPage>
            <Intro>
                <Title className="cds-t-d1">{title}</Title>
                <Prose>
                    {subtitle && <Message>{subtitle}</Message>}
                    <Message>{message}</Message>
                </Prose>
            </Intro>
            <H5 as="h2">Client Inquiries / Support</H5>
            <StyledContactDetails />
            {showDebugInfo && (
                <>
                    <Notification>
                        Please ensure your browser is up to date and try again before reporting this issue.
                    </Notification>
                    <DebugInfo />
                </>
            )}
        </StyledErrorPage>
    </CenteredPageContent>
);
